import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { formatApiErrorDetail } from "./api";

export const Login = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(form.email.trim(), form.password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white grid place-items-center px-6 py-12 font-body">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-zinc-500 hover:text-brand-light mb-10">
          ← Back to Site
        </Link>
        <div className="overline">Admin · Control Panel</div>
        <h1 className="font-display text-5xl uppercase mt-4 leading-[0.95]">
          Sign in to <br />
          <span className="text-brand-light">BDH</span>
        </h1>
        <p className="mt-4 text-sm text-zinc-400 max-w-sm">
          Manage hero copy, programs, posts, and site settings.
        </p>

        <form onSubmit={onSubmit} className="mt-10 bento-card p-6 md:p-8" data-testid="admin-login-form">
          <label className="flex flex-col gap-2 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">Email</span>
            <input
              type="email"
              autoComplete="username"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="admin@builtdifferenthockey.com"
              required
              className="bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-3 text-white placeholder:text-zinc-600 transition-colors"
              data-testid="admin-login-email"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              className="bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-3 text-white placeholder:text-zinc-600 transition-colors"
              data-testid="admin-login-password"
            />
          </label>

          {error && (
            <div className="mt-5 text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-4 py-3" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-4">
            <button
              type="button"
              disabled
              className="text-xs tracking-[0.2em] uppercase text-zinc-600 cursor-not-allowed"
              title="Google sign-in — coming in Phase 2"
              data-testid="admin-login-google"
            >
              Google sign-in (soon)
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
              data-testid="admin-login-submit"
            >
              {submitting ? (<><Loader2 className="h-5 w-5 animate-spin" /> Signing in</>) : (<>Sign In <ArrowUpRight className="h-5 w-5" /></>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
